import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AppShell } from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ClientesPage from './pages/ClientesPage';
import ProyectosPage from './pages/ProyectosPage';
import CotizacionesPage from './pages/CotizacionesPage';
import ContenedoresPage from './pages/ContenedoresPage';
import ContenedorEditorPage from './pages/ContenedorEditorPage';
import CatalogoPage from './pages/CatalogoPage';
import CatalogoContenedoresPage from './pages/CatalogoContenedoresPage';
import UsuariosPage from './pages/UsuariosPage';
import EditorPage from './pages/EditorPage';
import PortalPage from './pages/PortalPage';
import ReportesPage from './pages/ReportesPage';
import ComparadorPage from './pages/ComparadorPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import MobileCotizacionesPage from './pages/mobile/MobileCotizacionesPage';
import MobileCotizacionDetallePage from './pages/mobile/MobileCotizacionDetallePage';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30 * 1000 },
  },
});

// Redirección automática a vistas móviles read-only (<768px)
function MobileRedirector() {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    const check = () => {
      if (window.innerWidth >= 768) return;
      const p = location.pathname;
      if (p === '/cotizaciones' || p === '/cotizaciones-pv') {
        navigate('/m/cotizaciones', { replace: true });
        return;
      }
      const m = p.match(/^\/cotizaciones\/([^/]+)\/editor$/);
      if (m) navigate(`/m/cotizaciones/${m[1]}`, { replace: true });
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [location.pathname, navigate]);
  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <MobileRedirector />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/portal/:token" element={<PortalPage />} />
              <Route element={<AppShell />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/clientes" element={<ClientesPage />} />
                <Route path="/proyectos" element={<ProyectosPage />} />
                <Route path="/cotizaciones" element={<CotizacionesPage quotationType="prefab" />} />
                <Route path="/cotizaciones-pv" element={<CotizacionesPage quotationType="pv" key="pv" />} />
                <Route path="/cotizaciones/:id/editor" element={<EditorPage />} />
                <Route path="/contenedores" element={<ContenedoresPage />} />
                <Route path="/contenedores/:id" element={<ContenedorEditorPage />} />
                <Route path="/catalogo" element={<CatalogoPage scope="prefab" key="prefab" />} />
                <Route path="/catalogo-pv" element={<CatalogoPage scope="pv" key="pv-cat" />} />
                <Route path="/catalogo-contenedores" element={<CatalogoContenedoresPage />} />
                <Route path="/usuarios" element={<UsuariosPage />} />
                <Route path="/configuracion" element={<ConfiguracionPage />} />
                <Route path="/reportes" element={<ReportesPage />} />
                <Route path="/comparador" element={<ComparadorPage />} />
                <Route path="/m/cotizaciones" element={<MobileCotizacionesPage />} />
                <Route path="/m/cotizaciones/:id" element={<MobileCotizacionDetallePage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
